import React, { Component, useEffect } from "react";

// third party
import { connect } from "react-redux";
import history from "./history";
import { Router, Route, Redirect, Switch, Link } from "react-router-dom";
import { withScope, captureException } from "@sentry/browser";
import _ from "lodash";
import PubSub from "pubsub-js";
import SatismeterService from "./services/SatismeterService";
import moment from "moment";
import {
	Snowfall,
	TopNavbar,
	TopNavbarApps,
	DiscoverApps,
	HorizonButton,
	HorizonText,
	TextVariant,
	HorizonTooltip
} from "@urbanpiper-engineering/horizon";

// utils
import {
	isLoggedin,
	lS,
	scroll,
	isFeatureFlagEnabled,
	isChristmasSeason,
	isStartOfNewYear,
	trackEvent,
	openFreshchatWidget,
	capitaliseTextStrict,
	isInternalUser,
	getLoggedInBizId,
	sortArrayOfObjects,
	getKeyForOS
} from "./atlas-utils";
import { initFreshChat } from "./atlas-utils/init";
import { UPDATE_USER_PROPERTIES } from "./atlas-utils/tracking";

// actions
import { ActionTypes } from "./actions/_types";
import { updateBizState, toggleGlobalLoader } from "./actions/actions";
import { fetchHubConfigurations } from "./actions/hub";
import { subscribeToFlowSteps, unsubscribeToFlowSteps, fetchOnboardingFlows } from "./actions/onboarding";
import { periscopeAutoRefresh } from "./actions/periscope";

// components
import {
	GlobalMessage,
	GlobalDeactivationBlocker,
	ShowCloseDrawerMessage,
	toggleSidebar,
	logout,
	InfiniteLoader
} from "./components/SiteComp";
import TopBanner from "./components/_commons/TopBanner";
import SideNavContainer, { LogoBlock } from "./components/_commons/SideNavContainer";
import Rewind from "./containers/Rewind";
import Login from "./containers/Login";
import AutoLogin from "./containers/AutoLogin";
import AuthServiceLogin from "./containers/AuthServiceLogin";
import Dashboard from "./containers/Dashboard";
import CampaignContainer from "./containers/Campaigns";
import SegmentsContainer from "./containers/Segments";
import { BusinessProfile } from "./containers/BusinessProfile";
import BillingContainer from "./components/Settings/BillingSettings";
import UserManagementContainer from "./components/Settings/UserManagementSettings";
import UnifiedUserManagementContainer from "./components/Settings/UnifiedUserManagement";
import APIAccessManagementContainer from "./components/Settings/APIAccessManagementSettings";
import TransactionsContainer from "./containers/Transactions";
import CustomersContainer from "./containers/Customers";
import AnalyticsContainer from "./containers/Analytics";
import SalesAnalytics from "./containers/SalesAnalytics";
import Operations from "./containers/Operations";
import RhiTransactions from "./containers/RhiTransactions";
import ErrorPage from "./containers/ErrorPage";
import AccessDenied from "./containers/AccessDenied";
import LocationsContainer from "./containers/Locations";
import CategoriesContainer from "./containers/Categories";
import ItemsContainer from "./containers/Items";
import ModifierGroupsContainer from "./containers/ModifierGroups";
import ModifiersContainer from "./containers/Modifiers";
import TaxesContainer from "./containers/Taxes";
import ItemGroupsContainer from "./containers/ItemGroups";
import LocationGroupsContainer from "./containers/LocationGroups";
import TimingGroupsContainer from "./containers/TimingGroups";
import ChargesContainer from "./containers/Charges";
import BulkOperationsContainer from "./containers/BulkOperations";
import MerakiContainer from "./containers/Meraki";
import Hub from "./containers/Hub";
import DiscountsContainer from "./containers/Discounts";
import ReferralsContainer from "./containers/Referrals";
import CouponsContainer from "./containers/Coupons";
import ItemTagsContainer from "./containers/ItemTags";
import CatalogueVerificationContainer from "./containers/CatalogueVerification";
import MerakiWebContainer from "./containers/MerakiWeb/index";
import MerakiPlansBilling from "./containers/MerakiPlansBilling";
import WebhooksContainer from "./containers/Webhooks";
import GalleryContainer from "./containers/Gallery";
import ReportsContainer from "./containers/Reports";
import ReportsNew from "./containers/ReportsNew";
import CustomerFeedbackContainer from "./containers/CustomerFeedback";
import CustomIntegrationsContainer from "./containers/CustomIntegrations";
import UserVerification from "./containers/UserVerification";
import AcceptInvite from "./containers/AcceptInvite";
import MerakiWebEditorContainer from "./containers/MerakiWebEditor";
import PiperAcademyContainer from "./containers/PiperAcademy";
import AuditEventsContainer from "./containers/AuditEvents";
import ReconciliationContainer from "./containers/Reconciliation";
import ReconciliationStats from "./containers/ReconciliationStats";
import DeveloperTools from "./containers/DeveloperTools";
import OngoingTasks from "./components/_commons/OngoingTasks";
import CatalogueBackupsContainer from "./containers/CatalogueBackups";
import Brands from "./containers/Brands";
import MenusContainer from "./containers/Menus";
import SectionsContainer from "./containers/Sections";
import Onboarding from "./containers/Onboarding";
import OnBoardingBrands from "./containers/OnBoardingBrands";
import OnboardingLocations from "./containers/OnboardingLocations";
import OnboardingMenus from "./containers/OnboardingMenus";
import BillingRecovery from "./containers/BillingRecovery";
import Home from "./containers/Persicope/Dashboard/Home";
import LocationAvailability from "./containers/Persicope/LocationAvailability/LocationAvailability";
import ItemAvailabilityRoutes from "./containers/Persicope/ItemAvailability/ItemAvailabilityRoutes";
import ItemIssuesRoutes from "./containers/Persicope/ItemIssues/ItemIssuesRoutes";
import ActivityLog from "./containers/Persicope/ActivityLog/ActivityLog";
import UserEarlyAccess from "./containers/Persicope/UserEarlyAccess/UserEarlyAccess";
import WaitingPage from "./containers/Persicope/UserEarlyAccess/WaitingPage";
import { ButtonIcon } from "./components/_commons/ButtonIcon";
import Notifications from "./containers/Notifications";
import PipSearch from "./containers/PipSearch";
import MerakiLanding from "./containers/MerakiWeb/MerakiLanding";
import PlatformApp from "./containers/PlatformApp";

// clients
import { store } from "./store/configureStore";
import { clientOnboarding, resetClientOnboarding, closeOnboardingWsLink } from "./client-onboarding";

// graphql
import { GET_INFO_FOR_MIXPANEL } from "./graphql/misc";

// services
import NotificationService from "./services/NotificationService";

// helpers
import getSideNavItems, { newNavOrder } from "./helpers/sideNav";
import getCreateFlows from "./helpers/pip";

// style
import "./css/main.scss";

// constants;
import { DEVELOPER_TOOLS_ENABLED_PRODUCTION_BIZ_IDS, TRACKING_EVENT_NAMES } from "./client-config";
import { ONBOARDING_FLOWS } from "./client-config";
const TRACKING_ENABLED = process.env.REACT_APP_TRACKING_ENABLED;
const ENVIRONMENT_FLAG = process.env.REACT_APP_SHOW_ENVIRONMENT_FLAG;
const ULE_SWITCH_APP_URL = process.env.REACT_APP_ULE_SWITCH_APP;

@connect((store) => ({
	globalMessage: store.globalMessage,
	dimensions: store.configItems.dimensions,
	showCloseDrawerMessage: store.showCloseDrawerMessage,
	verificationDetails: store.login.loginDetail.verificationDetails,
	login: store.login,
	atlasOnboardingflowData: store.atlasOnboardingState.flowData,
	periscopeEarlyAccessState: store.periscopeEarlyAccessState,
	billingRecoveryState: store.billingRecoveryState,
	bizPlatforms: store.configItems.bizPlatforms
}))
class App extends Component {
	constructor(props) {
		super(props);
	}

	componentDidCatch(error, errorInfo) {
		if (TRACKING_ENABLED === "yes") {
			withScope((scope) => {
				Object.keys(errorInfo).forEach((key) => {
					scope.setExtra(key, errorInfo[key]);
				});
				if (this.props?.login?.loggedInbizDetail?.id) {
					scope.setExtra("Biz", {
						id: this.props?.login?.loggedInbizDetail?.id,
						name: this.props?.login?.loggedInbizDetail?.name
					});
				}
				captureException(error);
			});
		}
	}

	updateDimensions = () => {
		store.dispatch({
			type: "UPDATE_DIMENSIONS",
			payload: {
				height: window.innerHeight,
				width: window.innerWidth
			}
		});
	};

	updateDimensionsDebounced = _.debounce(() => this.updateDimensions(), 500);

	getFreshChatInitData = () => {
		const freshChatInitData = {
			bizName: this.props.login?.loggedInbizDetail?.name,
			bizId: this.props.login?.loggedInbizDetail?.id,
			firstName: this.props.login?.loginDetail?.firstName,
			email: this.props.login?.loginDetail?.email,
			phone: this.props.login?.loginDetail?.phone,
			mode: "atlas",
			companyCountry: this.props.login?.loggedInbizDetail?.country,
			externalId: this.props.login?.authServiceData?.authUserId || this.props.login?.loginDetail?.id // ule userId takes the precedence
		};
		return freshChatInitData;
	};

	componentDidMount() {
		this.updateDimensions();
		window.addEventListener("resize", this.updateDimensionsDebounced);

		initFreshChat(this.getFreshChatInitData());
	}

	componentDidUpdate(prevProps) {
		if (window.fcWidget && !window?.fcWidget?.isInitialized()) {
			initFreshChat(this.getFreshChatInitData());
		}

		// start subscription for onboarding flow steps if data is available, and redirect to onboarding page
		if (
			clientOnboarding &&
			!["/login", "/temp-login", "/auth-service"].includes(window?.location?.pathname) &&
			this.props.atlasOnboardingflowData?.name === ONBOARDING_FLOWS.ATLAS_ONBOARDING_FLOW
		) {
			subscribeToFlowSteps("atlas-onboarding");

			// if user is not already on onboarding, redirect to onboarding
			if (
				this.props.atlasOnboardingflowData?.name !== prevProps.atlasOnboardingflowData?.name &&
				!window?.location?.pathname.includes("/onboarding")
			) {
				history.push("/onboarding");
			}
		}
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.updateDimensionsDebounced);

		// unsubscribe to onboarding flow steps
		unsubscribeToFlowSteps();
	}

	render() {
		const { show, message } = this.props.globalMessage;
		const { showInfo } = this.props.showCloseDrawerMessage;
		return (
			<Router history={history}>
				<div>
					{show && window.location.pathname !== "/login" ? (
						<GlobalMessage />
					) : message !== "" && message[0] === "Invalid Credentials\n" ? (
						<GlobalMessage />
					) : null}
					{NotificationService.renderGlobalNotificationsContainer}
					{showInfo && <ShowCloseDrawerMessage />}
					<Switch>
						<Route exact path="/login" component={Login} />
						<Route exact path="/temp-login" component={AutoLogin} />
						<Route exact path="/auth-service" component={AuthServiceLogin} />
						<Route exact path="/invite/:uuid/accept" component={AcceptInvite} />
						<Route
							exact
							path="/meraki-web/editor"
							render={(props) =>
								!isLoggedin() ? (
									<Redirect
										to={{
											pathname: "/login",
											state: props.location
										}}
									/>
								) : (
									<MerakiWebEditorContainer {...props} />
								)
							}
						/>
						<Route
							exact
							path="/user-verification"
							render={(props) =>
								!isLoggedin() ? (
									<Redirect
										to={{
											pathname: "/login",
											state: props.location
										}}
									/>
								) : this.props?.verificationDetails?.emailValidated &&
								  this.props?.verificationDetails?.phoneValidated ? (
									<Redirect
										to={{
											pathname: "/",
											state: props.location
										}}
									/>
								) : (
									<UserVerification {...props} />
								)
							}
						/>
						<Route
							path="/"
							render={(props) =>
								!isLoggedin() ? (
									<Redirect
										to={{
											pathname: "/login",
											state: props.location
										}}
									/>
								) : (!this.props?.verificationDetails?.emailValidated ||
										!this.props?.verificationDetails?.phoneValidated) &&
								  ENVIRONMENT_FLAG === "prod" ? (
									<Redirect
										to={{
											pathname: "/user-verification",
											state: props.location
										}}
									/>
								) : (
									<>
										<SecureRoutes {...props} updateDimensions={this.updateDimensions} />
										{isFeatureFlagEnabled("pip-search") && (
											<PipSearch
												navItems={getSideNavItems({
													biz: this.props.login?.loggedInbizDetail,
													access: this.props.login?.loginDetail?.access,
													merakiWebSubscription: this.props.login?.merakiWebSubscription,
													periscopeEarlyAccessState: this.props.periscopeEarlyAccessState,
													atlasOnboardingState: this.props.atlasOnboardingflowData,
													billingRecoveryState: this.props.billingRecoveryState,
													bizPlatforms: this.props.bizPlatforms
												})}
												createFlows={getCreateFlows({
													biz: this.props.login?.loggedInbizDetail,
													access: this.props.login?.loginDetail?.access,
													atlasOnboardingState: this.props.atlasOnboardingflowData
												})}
											/>
										)}
									</>
								)
							}
						/>
					</Switch>
				</div>
			</Router>
		);
	}
}

@connect((store) => ({
	login: store.login,
	banner: store.banner,
	dimensions: store.configItems.dimensions,
	outstandingInvoicesState: store.outstandingInvoicesState,
	biz: store.login.loggedInbizDetail,
	user: store.login.loginDetail,
	banner: store.banner,
	sidebarState: store.sidebarState,
	headwayWidgetState: store.headwayWidgetState,
	globalLoaderState: store.globalLoaderState,
	access: store.login.loginDetail.access,
	userEmail: store.login.loginDetail.email,
	snowfall: store.snowfallState,
	verificationList: store.catalogueVerificationList,
	merakiWebSubscription: store.merakiWebSubscription.data,
	showCustomBannerMessage: store.login.showCustomBannerMessage,
	isBannerMessageDismissed: store.login.isBannerMessageDismissed,
	catalogueBackupsStatus: store.catalogueBackupsStatus,
	periscopeEarlyAccessState: store.periscopeEarlyAccessState,
	atlasOnboardingState: store.atlasOnboardingState,
	billingRecoveryState: store.billingRecoveryState,
	periscopeDataResponse: store.periscopeDataResponse,
	freshChatWidgetState: store.freshChatWidgetState,
	bizPlatforms: store.configItems.bizPlatforms,
	pipSearchState: store.pipSearchState
}))
class SecureRoutes extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			endDate: new Date("Nov 10, 2021 23:59:59").getTime(),
			timer: undefined,
			isPeriscopeEarlyAccessEnabled: undefined,
			isPeriscopeEnabled: undefined,
			searchOpen: false,
			switchedToOldNavigation: lS.get("switchToOldNavigation")?.[getLoggedInBizId()] || false,
			showTour: false
		};
	}
	userPopoverRef = React.createRef();
	topNavbarAppsRef = React.createRef();
	pipSearchAppsRef = React.createRef();
	discoverAppsRef = React.createRef();

	async componentDidMount() {
		// this.countdown();
		this.props.updateDimensions();
		if (window?.posthog) {
			const isPeriscopeEnabled = window.posthog?.isFeatureEnabled("enable-periscope");
			const isEarlyAccessFeatureEnabled = window.posthog?.isFeatureEnabled("periscope-early-access");

			this.setState({
				isPeriscopeEarlyAccessEnabled: isEarlyAccessFeatureEnabled,
				isPeriscopeEnabled: isPeriscopeEnabled
			});
		}

		this.handleSidebarState();

		// start client onboarding and fetch flows
		await resetClientOnboarding();
		fetchOnboardingFlows();

		await updateBizState(this.props?.login);

		// show new navigation tour after updating biz state
		setTimeout(() => {
			if (!this.props?.billingRecoveryState?.modal?.isOpen) {
				this.setState({ showTour: true });
			}
		}, 2000);

		// initialize user data in satismeter
		SatismeterService.userInit(this.props?.login);

		if (!this.props.access.isNonHqAnalytics && !this.props.access.isNonHqReport) {
			fetchHubConfigurations();
		}

		// try {
		// 	// get additional info for mixpanel
		// 	const { data } = await client.query({
		// 		query: GET_INFO_FOR_MIXPANEL,
		// 		variables: {
		// 			brand: "all"
		// 		}
		// 	});
		// 	if (data) {
		// 		const isMultibrandEnabled = this.props.biz.isMultibrandEnabled;
		// 		const eventMeta = {
		// 			num_of_physical_locations_atlas: data?.physicalLocations?.count,
		// 			num_of_brand_locations_atlas: isMultibrandEnabled
		// 				? data?.brandLocations?.count
		// 				: data?.physicalLocations?.count,
		// 			num_of_brands: data?.brands?.count,
		// 			platforms_enabled: data?.configurations?.map((platform) => platform?.name)
		// 		};
		// 		PubSub.publish(UPDATE_USER_PROPERTIES, eventMeta);
		// 	}
		// } catch (err) {
		// 	// do nothing
		// }
	}

	periscopeAutoRefreshHandler() {
		if (
			window.location.pathname.startsWith("/periscope/") &&
			!this.fetchPeriscopeInterval
			// && this.props.periscopeDataResponse.autoRefresh
		) {
			periscopeAutoRefresh(true);
			this.fetchPeriscopeInterval = setInterval(
				() => {
					periscopeAutoRefresh(true);
				},
				5 * 60 * 1000
			);
		} else {
			if (
				// !this.props.periscopeDataResponse.autoRefresh ||
				this.fetchPeriscopeInterval &&
				!window.location.pathname.startsWith("/periscope/")
			) {
				clearInterval(this.fetchPeriscopeInterval);
				this.fetchPeriscopeInterval = null;
			}
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.dimensions.width > 768 && this.props.dimensions.width <= 768) {
			toggleSidebar(true);
		}
		if (prevProps.dimensions.width <= 768 && this.props.dimensions.width > 768) {
			toggleSidebar(false);
		}
		if (prevProps.billingRecoveryState?.modal?.isOpen && !this.props.billingRecoveryState?.modal?.isOpen) {
			setTimeout(() => {
				this.setState({ showTour: true });
			}, 2000);
		}
		if (
			window.location.pathname.startsWith("/periscope/") &&
			prevProps.periscopeDataResponse.autoRefresh !== this.props.periscopeDataResponse.autoRefresh
		) {
			this.periscopeAutoRefreshHandler();
		}
		this.periscopeAutoRefreshHandler();
	}

	componentWillUnmount() {
		clearInterval(this.interval);
	}

	handleSidebarState = () => {
		if (this.props.dimensions.width <= 768) {
			toggleSidebar(true);
		}
		if (this.props.dimensions.width > 768) {
			toggleSidebar(false);
		}
	};

	handleOpenUrl = (url, target = "_self") => {
		if (url) {
			store.dispatch(toggleGlobalLoader(true));

			// onboarding
			unsubscribeToFlowSteps();
			closeOnboardingWsLink();

			// track event
			const eventName = "switch_app";
			const eventMeta = {};
			trackEvent(eventName, eventMeta);

			window.open(url, target).focus();
		}
	};

	handleOpenPipSearch = () => {
		store.dispatch({
			type: ActionTypes.UPDATE_PIP_SEARCH_STATE,
			payload: { open: true }
		});
		this.setState({
			searchOpen: false
		});
		trackEvent(TRACKING_EVENT_NAMES.PIP_OPEN, { src: "icon" });
	};

	handlePipSearchTooltip = (open) => {
		this.setState({
			searchOpen: open
		});
	};

	openProfile = () => {
		history.push("/profile");
	};

	getApps = () => {
		const apps = getSideNavItems({
			biz: this.props.biz,
			access: this.props.access,
			merakiWebSubscription: this.props.merakiWebSubscription,
			periscopeEarlyAccessState: this.props.periscopeEarlyAccessState,
			atlasOnboardingState: this.props.atlasOnboardingState,
			billingRecoveryState: this.props.billingRecoveryState,
			bizPlatforms: this.props.bizPlatforms
		});
		const filteredApps = apps
			.map((app) => {
				if (app?.childrenProps?.length) {
					app.childrenProps = app.childrenProps.filter((child) => !!child?.render);
				}
				return app;
			})
			.filter((app) => !!app?.render);

		return filteredApps;
	};

	updateSelectedApp = (app) => {
		const apps = this.getApps();
		const parentNav = apps?.find((item) => item?.key === app?.[0]);
		const getNavWithPath = parentNav?.childrenProps?.length
			? parentNav?.childrenProps?.find((nav) => nav.key === app?.[app?.length - 1])
			: parentNav;

		if (getNavWithPath?.path) {
			store.dispatch({
				type: "UPDATE_OPEN_ITEMS",
				payload: app
			});
			history.push(getNavWithPath.path);
		}

		if (getNavWithPath?.onClick) {
			getNavWithPath.onClick();
		}
	};

	renderLeftBlock = () => {
		const isNavigationV2 = isFeatureFlagEnabled("atlas-uns-navigation-v2") && !this.state.switchedToOldNavigation;
		if (isNavigationV2) {
			const apps = this.getApps() || [];
			const sortedApps = sortArrayOfObjects(apps, newNavOrder, "key");
			return (
				<div className="flex items-center" ref={this.topNavbarAppsRef}>
					<TopNavbarApps
						apps={sortedApps.slice(0, 5)}
						selectedApp={this.props.sidebarState?.openItems}
						onAppClick={this.updateSelectedApp}
					/>
					<DiscoverApps
						apps={sortedApps.slice(5)}
						selectedApp={this.props.sidebarState?.openItems}
						onAppClick={this.updateSelectedApp}
						ref={this.discoverAppsRef}
					/>
				</div>
			);
		}
		return null;
	};

	renderRightBlock = () => {
		const headwayWidgetState = this.props.headwayWidgetState;
		return (
			<div className="top-navbar-icons right-block">
				<HorizonTooltip
					open={this.state.searchOpen}
					trigger={["hover"]}
					onOpenChange={this.handlePipSearchTooltip}
					title={
						<HorizonText className="text-up-black-80 p-1 flex gap-2" variant={TextVariant.BODY2_SEMIBOLD}>
							Search
							{getKeyForOS() && (
								<HorizonText
									variant={TextVariant.BODY2_SEMIBOLD}
									className="text-up-black-50 m-0"
									inline
								>
									{getKeyForOS()} K
								</HorizonText>
							)}
						</HorizonText>
					}
					placement="bottom"
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
				>
					<div className="pip-search" ref={this.pipSearchAppsRef}>
						<ButtonIcon
							classes={this.props.pipSearchState.open ? "selected" : ""}
							icon={"search-lens"}
							color={this.props.pipSearchState?.open ? "#3F66FF" : "#8a8a8a"}
							clickHandler={this.handleOpenPipSearch}
						/>
						<div className="new">
							<img src="/assets/icons/icon-sparkles.svg" alt="" />
							<span>New</span>
						</div>
					</div>
				</HorizonTooltip>
				<HorizonTooltip
					trigger={["hover"]}
					title={
						<HorizonText className="text-up-black-80 p-1" variant={TextVariant.BODY2_SEMIBOLD}>
							Announcements
						</HorizonText>
					}
					placement="bottom"
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
				>
					<div className={"meraki-changelog" + (headwayWidgetState?.active ? " selected" : "")}>
						<div className="icon-changelog">
							<ButtonIcon
								icon={"announcement" + (headwayWidgetState?.active ? "-selected" : "")}
								color={headwayWidgetState?.active ? "#3F66FF" : "#8a8a8a"}
							/>
						</div>
					</div>
				</HorizonTooltip>
				<Notifications />
				<HorizonTooltip
					trigger={["hover"]}
					title={
						<HorizonText className="text-up-black-80 p-1" variant={TextVariant.BODY2_SEMIBOLD}>
							Help
						</HorizonText>
					}
					placement="bottom"
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
				>
					<Link to="/piper-academy" onClick={() => trackEvent("clicked_get_started_with_atlas", {})}>
						<ButtonIcon
							classes={window?.location?.pathname?.includes("/piper-academy") ? "selected" : ""}
							icon={"help" + (window?.location?.pathname?.includes("/piper-academy") ? "-selected" : "")}
							color={window?.location?.pathname?.includes("/piper-academy") ? "#3F66FF" : "#8a8a8a"}
						/>
					</Link>
				</HorizonTooltip>
				<HorizonTooltip
					trigger={["hover"]}
					title={
						<HorizonText className="text-up-black-80 p-1" variant={TextVariant.BODY2_SEMIBOLD}>
							Support
						</HorizonText>
					}
					placement="bottom"
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
				>
					<div>
						<ButtonIcon
							classes={this.props.freshChatWidgetState?.busy ? "selected" : ""}
							icon={"call-chat-support" + (this.props.freshChatWidgetState?.busy ? "-selected" : "")}
							color={this.props.freshChatWidgetState?.busy ? "#3F66FF" : "#8a8a8a"}
							clickHandler={() => openFreshchatWidget()}
						/>
					</div>
				</HorizonTooltip>
			</div>
		);
	};

	renderCenterBlock = () => {
		return (
			<HorizonText variant={TextVariant.BODY1_SEMIBOLD} className="center-block-breadcrumbs text-xs">
				<span>
					{capitaliseTextStrict(this.props.sidebarState?.openItems?.[0]?.split("-")?.join(" "), true)}
				</span>
				{this.props.sidebarState?.openItems?.[1] && (
					<>
						<span>{" / "}</span>
						<HorizonText variant={TextVariant.BODY1} className="text-xs text-up-black-50" inline>
							{capitaliseTextStrict(this.props.sidebarState?.openItems?.[1]?.split("-")?.join(" "), true)}
						</HorizonText>
					</>
				)}
			</HorizonText>
		);
	};

	handleHidePopover = (action) => {
		if (action === "old-navigation") {
			// update local storage to switch to old navigation
			const oldNavigationState = lS.get("switchToOldNavigation") || {};
			const bizId = getLoggedInBizId();
			lS.set("switchToOldNavigation", {
				...oldNavigationState,
				[bizId]: true
			});

			// update state
			toggleSidebar(false);
			this.setState({ switchedToOldNavigation: true });

			// trigger survey
			SatismeterService.satismeterEvent("switchedToOldNavigation");
		}
		if (action === "new-navigation") {
			// update local storage to switch to new navigation
			const oldNavigationState = lS.get("switchToOldNavigation") || {};
			const bizId = getLoggedInBizId();
			lS.set("switchToOldNavigation", {
				...oldNavigationState,
				[bizId]: false
			});

			// update state
			this.setState({ switchedToOldNavigation: false });
		}

		// close popover
		if (this.userPopoverRef?.current) {
			this.userPopoverRef.current.hide();
		}
	};

	renderadditionalPopoverContent = () => {
		const { switchedToOldNavigation } = this.state;
		const isNavigationV2 = isFeatureFlagEnabled("atlas-uns-navigation-v2");
		if (isNavigationV2) {
			return (
				<>
					{!switchedToOldNavigation && (
						<div className="px-4 py-3">
							<HorizonButton
								type="link"
								onClick={() => this.handleHidePopover("old-navigation")}
								className="p-0 !no-underline text-sm !text-up-blue-40 h-fit"
							>
								Switch to older navigation
							</HorizonButton>
						</div>
					)}
					{switchedToOldNavigation && (
						<div className="px-4 py-3">
							<HorizonButton
								type="link"
								onClick={() => this.handleHidePopover("new-navigation")}
								className="p-0 !no-underline text-sm !text-up-blue-40 h-fit"
							>
								Switch to new navigation
							</HorizonButton>
						</div>
					)}
				</>
			);
		}
		return null;
	};

	closeTour = () => {
		// update state
		this.setState({ showTour: false });

		// mark tour as shown
		const coachMarkState = lS.get("coachmarkState") || {};
		const bizId = getLoggedInBizId();
		lS.set("coachmarkState", {
			...coachMarkState,
			[bizId]: {
				...(coachMarkState?.bizId ?? {}),
				newNavigationTour: true
			}
		});
	};

	countdown = () => {
		this.interval = setInterval(() => {
			const now = new Date().getTime();
			const timeleft = this.state.endDate - now;
			if (timeleft < 0) {
				this.setState({ timer: undefined });
				clearInterval(this.interval);
				return;
			}

			// set timer in days, hours, minutes and seconds
			const timer = {
				days: Math.floor(timeleft / (1000 * 60 * 60 * 24)),
				hours: Math.floor((timeleft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
				minutes: Math.floor((timeleft % (1000 * 60 * 60)) / (1000 * 60)),
				seconds: Math.floor((timeleft % (1000 * 60)) / 1000)
			};
			this.setState({ timer });
		}, 1000);
	};

	render() {
		const {
			login,
			dimensions,
			access = {},
			userEmail = "",
			banner,
			merakiWebSubscription,
			showCustomBannerMessage,
			isBannerMessageDismissed,
			catalogueBackupsStatus,
			sidebarState,
			...rest
		} = this.props;
		const {
			modal: { isWebhookDisabled }
		} = this.props.billingRecoveryState;
		const { data, loading, error } = this.props.outstandingInvoicesState;
		let outstandingInvoices = data.objects || [];
		let pendingInvoices = (data.objects || []).sort((a, b) => a.dueDate.valueOf() - b.dueDate.valueOf());
		const currencySymbol = this.props.biz.currencySymbol || "";
		const bizState = this.props.biz.currentState;
		const bizId = this.props?.biz?.id;
		const bizCountry = this.props?.biz?.country;
		const isReconEnabled = this.props.biz.isReconEnabled;
		const isMultibrandEnabled = this.props.biz.isMultibrandEnabled;
		const isMenuOverCatalogueEnabled = this.props.biz.isMenuOverCatalogueEnabled;
		const isBizDeactivated = bizState === "inactive" || bizState === "pending_deactivation";
		const isOnboardingFlowEnabled =
			this.props.atlasOnboardingState?.flowData?.name === ONBOARDING_FLOWS.ATLAS_ONBOARDING_FLOW;
		const showFullNav = this.props?.atlasOnboardingState?.flowSteps?.find(
			(step) => step?.step?.name === "SHOW_FULL_NAV"
		)
			? true
			: false;
		let bannerstyles = {};
		if (showCustomBannerMessage && banner.filter((b) => !b.isDismissed).length > 0) {
			bannerstyles = { paddingTop: "120px" };
		} else if (outstandingInvoices.length > 0 || bizState === "pending_activation") {
			bannerstyles = { paddingTop: "120px" };
		} else if (merakiWebSubscription?.subscription?.status === "trialing") {
			bannerstyles = { paddingTop: "120px" };
		}
		// need to remove in next release
		const todaysDate = moment();
		const targetDate = moment("15-03-2024", "DD-MM-YYYY");
		const isDateBeforeTargetDate = todaysDate.isBefore(targetDate);
		const withBanner = dimensions?.width > 768 && bannerstyles?.paddingTop;

		const isNavigationV2 = isFeatureFlagEnabled("atlas-uns-navigation-v2") && !this.state.switchedToOldNavigation;
		const isNewNavigationTourShown = lS.get("coachmarkState")?.[getLoggedInBizId()]?.["newNavigationTour"] || false;
		const newNavigationTourSteps = [
			{
				id: "new-nav-intro",
				title: "Introducing our all new navigation!",
				description:
					"We’ve moved some items from the side nav to the top nav — so you'll now find them right where you need them.",
				target: () => this.topNavbarAppsRef?.current,
				placement: "bottomLeft"
			},
			{
				id: "all-apps",
				title: "All your Apps, still here!",
				description: "Some tools have moved to the new “Apps” menu — neat and out of the way.",
				target: () => this.discoverAppsRef?.current,
				placement: "bottomLeft"
			},
			{
				id: "switch-to-old-nav",
				title: "Not ready for the new look yet?",
				description: "Go back to previous navigation — you can return here anytime.",
				target: () => this.userPopoverRef?.current?.element,
				placement: "bottomRight"
			}
		];

		return (
			<div>
				{dimensions.width > 768 && (
					<TopBanner showCustomBannerMessage={showCustomBannerMessage} banners={banner} />
				)}
				<TopNavbar
					className="atlas-top-navbar"
					profile={{ biz: this.props?.biz, user: this.props?.user }}
					appLogo={<LogoBlock redirectTo={isOnboardingFlowEnabled ? "/onboarding" : "/"} />}
					onMenuClick={
						isNavigationV2 || dimensions?.width > 768
							? () => toggleSidebar(!sidebarState.active)
							: undefined
					}
					showBizLogo={false}
					showProfileDetails={false}
					showUserInitials={true}
					onUserInfoClick={this.openProfile}
					showAppSwitcher={this.props?.login?.authServiceData?.userBizSwitch || false}
					onAppSwitcherClick={() => this.handleOpenUrl(ULE_SWITCH_APP_URL)}
					onLogoutClick={() => logout(this.props?.login?.authServiceData, this.props?.user?.id)}
					leftBlock={this.renderLeftBlock()}
					// centerBlock={dimensions?.width > 768 ? this.renderCenterBlock() : null}
					rightBlock={dimensions?.width > 768 ? this.renderRightBlock() : null}
					userPopoverRef={this.userPopoverRef}
					additionalPopoverContent={this.renderadditionalPopoverContent()}
					style={{ top: withBanner ? "57px" : "0px" }}
					v2={isNavigationV2}
					collapsed={sidebarState?.active}
					tourProps={{
						open: isNavigationV2 && !isNewNavigationTourShown && this.state.showTour,
						steps:
							this.getApps()?.length <= 5
								? newNavigationTourSteps?.filter((step) => step?.id !== "all-apps")
								: newNavigationTourSteps,
						onClose: this.closeTour,
						onFinish: this.closeTour,
						closeIcon: false,
						disabledInteraction: true
					}}
				/>
				<div
					className={
						"global-loader-container " +
						(this.props.globalLoaderState.active ? "active " : "") +
						(withBanner ? "with-banner " : "")
					}
				>
					<InfiniteLoader />
				</div>
				<div
					className={
						"layout-container " +
						(sidebarState?.active ? "minimized " : "") +
						(isNavigationV2 ? "v2 " : "") +
						"secure-routes"
					}
				>
					<SideNavContainer
						toggleCollapsed={() => toggleSidebar(!sidebarState?.active)}
						collapsed={sidebarState?.active}
						classes={withBanner ? "with-banner" : ""}
						isNavigationV2={isNavigationV2}
					/>
					<OngoingTasks />
					{isStartOfNewYear() && access.isAdmin && <Rewind />}
					{isChristmasSeason() && <Snowfall active={rest?.snowfall?.active} count={75} color="#c6d2ff" />}
					<BillingRecovery />
					<div
						className={"page-container" + (withBanner ? " with-banner" : "")}
						style={dimensions.width > 768 ? bannerstyles : {}}
					>
						<GlobalDeactivationBlocker
							currentPathname={this.props.location.pathname}
							currencySymbol={currencySymbol}
							outstandingInvoices={outstandingInvoices}
							show={isBizDeactivated}
						/>
						{!access.isNonHqReport && !access.isNonHqAnalytics ? (
							<div className="content-container">
								<Switch>
									<Route
										exact
										path="/"
										render={() =>
											access.isSalesAnalysis ? (
												isDateBeforeTargetDate ? (
													<Dashboard />
												) : isOnboardingFlowEnabled && !showFullNav ? (
													<Redirect to="/onboarding" />
												) : (
													<Redirect to="/orders" />
												)
											) : (
												<Redirect
													to="/locations"
													restrictView={catalogueBackupsStatus.restrict}
												/>
											)
										}
									/>
									<Route
										path="/onboarding"
										exact
										render={() =>
											isOnboardingFlowEnabled ? <Onboarding /> : <Redirect to="/orders" />
										}
									/>
									<Route
										path="/onboarding/brands"
										exact
										render={() =>
											isOnboardingFlowEnabled ? <OnBoardingBrands /> : <Redirect to="/orders" />
										}
									/>
									<Route
										path="/onboarding/locations"
										exact
										render={() =>
											isOnboardingFlowEnabled ? (
												<OnboardingLocations />
											) : (
												<Redirect to="/orders" />
											)
										}
									/>
									<Route
										path="/onboarding/menu"
										exact
										render={() =>
											isOnboardingFlowEnabled ? <OnboardingMenus /> : <Redirect to="/orders" />
										}
									/>
									<Route
										path="/campaigns"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : (
												<CampaignContainer />
											)
										}
									/>
									<Route
										path="/segments"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : (
												<SegmentsContainer />
											)
										}
									/>
									<Route
										path="/profile"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin ? (
												<BusinessProfile />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/billing"
										render={(props) => {
											return access.isBilling ? (
												<BillingContainer
													isBizDeactivated={isBizDeactivated}
													location={this.props.location}
													renderRechargeDrawer={props?.history?.action === "REPLACE"}
												/>
											) : (
												<Redirect to="/access-denied" />
											);
										}}
									/>
									<Route
										path="/recharge"
										render={() => (
											<Redirect
												to={{
													pathname: "/billing"
												}}
											/>
										)}
									/>
									<Route
										path="/access"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin && ENVIRONMENT_FLAG === "staging" ? (
												<UserManagementContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/unified-access"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin ? (
												<UnifiedUserManagementContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/api-access"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin ? (
												<APIAccessManagementContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/meraki"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : this.props.biz.modulesEnabled.includes("Meraki") &&
											  !(
													merakiWebSubscription?.subscription === null ||
													["cancelled", "not-subscribed"].includes(
														merakiWebSubscription?.subscription?.status
													)
											  ) ? (
												<MerakiContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/meraki-web"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : this.props.biz.modulesEnabled.includes("Meraki") ? (
												<MerakiWebContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/meraki-plans"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={
														{
															pathname: "/",
															state: props.location
														}
														// } />) : (this.props.biz.modulesEnabled.includes('Meraki')) ?
													}
												/>
											) : this.props.biz.modulesEnabled.includes("Meraki") &&
											  (merakiWebSubscription?.subscription === null ||
													merakiWebSubscription?.subscription?.status !==
														"legacy-billing") ? (
												<MerakiPlansBilling />
											) : (
												<Redirect to="/meraki-web" />
											)
										}
									/>
									<Route
										path="/hub"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isHubManagement ? (
												<Hub />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/platform-apps/:platform"
										render={(props) => <PlatformApp {...props} />}
									/>
									<Route
										path="/verification"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<CatalogueVerificationContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/activity-history"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin || access.isOperationsAnalysis || access.isSupport ? (
												<AuditEventsContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/feedback"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : this.props.biz.modulesEnabled.includes("Meraki") &&
											  (access.isSupport || access.isMarketing) ? (
												<CustomerFeedbackContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/orders"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isSupport ||
											  access.isSalesAnalysis ||
											  access.isOperationsAnalysis ? (
												<TransactionsContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/customers"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isSupport || access.isMarketing ? (
												<CustomersContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/brands"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : isMultibrandEnabled ? (
												<Brands
													hasAccess={access.isAdmin}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/locations"
										render={(props) =>
											isBizDeactivated ? (
												access.isSalesAnalysis ? (
													<Redirect
														to={{
															pathname: "/",
															state: props.location
														}}
													/>
												) : (
													<Redirect to="/access-denied" />
												)
											) : (
												<LocationsContainer
													hasAccess={access.isAdmin}
													bizCountry={bizCountry}
													isInternalUser={userEmail?.includes("@urbanpiper.com")}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											)
										}
									/>
									<Route
										path="/menus"
										render={(props) =>
											isBizDeactivated ? (
												access.isSalesAnalysis ? (
													<Redirect
														to={{
															pathname: "/",
															state: props.location
														}}
													/>
												) : (
													<Redirect to="/access-denied" />
												)
											) : access.isClosedBetaAccess && isMenuOverCatalogueEnabled ? (
												<MenusContainer hasAccess={access.isAdmin} />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/sections"
										render={(props) =>
											isBizDeactivated ? (
												access.isSalesAnalysis ? (
													<Redirect
														to={{
															pathname: "/",
															state: props.location
														}}
													/>
												) : (
													<Redirect to="/access-denied" />
												)
											) : access.isClosedBetaAccess && isMenuOverCatalogueEnabled ? (
												<SectionsContainer hasAccess={access.isAdmin} />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/item-groups"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<ItemGroupsContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/location-groups"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<LocationGroupsContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/timing-groups"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<TimingGroupsContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route path="/meraki-landing" render={(props) => <MerakiLanding />} />
									<Route
										path="/discounts"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isHubManagement ? (
												<DiscountsContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/coupons"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : (
												<CouponsContainer />
											)
										}
									/>
									<Route
										path="/referrals"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : (
												<ReferralsContainer />
											)
										}
									/>
									<Route
										path="/payout-sheets"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : isReconEnabled ? (
												<ReconciliationContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/reconciliation-stats"
										render={(props) =>
											(
												isBizDeactivated ? (
													<Redirect
														to={{
															pathname: "/",
															state: props.location
														}}
													/>
												) : (
													isReconEnabled
												)
											) ? (
												<ReconciliationStats />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/categories"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<CategoriesContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/items"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<ItemsContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/modifier-groups"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<ModifierGroupsContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/modifiers"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<ModifiersContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/taxes"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<TaxesContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/charges"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<ChargesContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/tags"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<ItemTagsContainer
													hasAccess={access.isCatalogueManagement}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/backups"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement || access.isAdmin ? (
												<CatalogueBackupsContainer
													hasAccess={access.isCatalogueManagement || access.isAdmin}
													restrictView={catalogueBackupsStatus.restrict}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/bulk"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isCatalogueManagement ? (
												<BulkOperationsContainer
													restrictView={catalogueBackupsStatus.restrict}
													bizCountry={bizCountry}
													isInternalUser={userEmail?.includes("@urbanpiper.com")}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/analytics"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : ENVIRONMENT_FLAG !== "prod" ||
											  access.isSalesAnalysis ||
											  access.isOperationsAnalysis ||
											  access.isMarketing ? (
												<AnalyticsContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/webhooks"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin && (!isWebhookDisabled || isInternalUser()) ? (
												<WebhooksContainer hasAccess={access.isAdmin} />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/gallery"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin ? (
												<GalleryContainer hasAccess={access.isAdmin} />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/integrations"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin && rest.biz.eisEnabled ? (
												<CustomIntegrationsContainer hasAccess={access.isAdmin} />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/developer-tools"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isAdmin &&
											  (ENVIRONMENT_FLAG === "posint" ||
													(ENVIRONMENT_FLAG === "prod" &&
														DEVELOPER_TOOLS_ENABLED_PRODUCTION_BIZ_IDS[bizId])) ? (
												<DeveloperTools hasAccess={access.isAdmin} />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/reports"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : access.isSalesAnalysis || access.isOperationsAnalysis ? (
												<ReportsNew
													hasAccess={access.isSalesAnalysis || access.isOperationsAnalysis}
												/>
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										exact
										path="/piper-academy"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : (
												<PiperAcademyContainer {...props} />
											)
										}
									/>
									<Route
										exact
										path="/piper-academy/:id"
										render={(props) =>
											isBizDeactivated ? (
												<Redirect
													to={{
														pathname: "/",
														state: props.location
													}}
												/>
											) : (
												<PiperAcademyContainer {...props} />
											)
										}
									/>
									<Route
										exact
										path="/periscope/home"
										render={(props) =>
											this.state.isPeriscopeEnabled &&
											(this.props.periscopeEarlyAccessState?.bizSubscription ||
												lS?.get("periscope")?.data?.[bizId]?.bizSubscription) ? (
												<Home />
											) : (
												this.state.isPeriscopeEarlyAccessEnabled !== undefined && (
													<Redirect to="/access-denied" />
												)
											)
										}
									/>
									<Route
										path="/periscope/item-availability"
										render={(props) =>
											this.state.isPeriscopeEnabled &&
											(this.props.periscopeEarlyAccessState?.bizSubscription ||
												lS?.get("periscope")?.data?.[bizId]?.bizSubscription) ? (
												<ItemAvailabilityRoutes />
											) : (
												this.state.isPeriscopeEarlyAccessEnabled !== undefined && (
													<Redirect to="/access-denied" />
												)
											)
										}
									/>
									<Route
										exact
										path="/periscope/storefront-availability"
										render={(props) =>
											this.state.isPeriscopeEnabled &&
											(this.props.periscopeEarlyAccessState?.bizSubscription ||
												lS?.get("periscope")?.data?.[bizId]?.bizSubscription) ? (
												<LocationAvailability />
											) : (
												this.state.isPeriscopeEarlyAccessEnabled !== undefined && (
													<Redirect to="/access-denied" />
												)
											)
										}
									/>
									<Route
										path="/periscope/item-issues"
										render={(props) =>
											this.state.isPeriscopeEnabled &&
											(this.props.periscopeEarlyAccessState?.bizSubscription ||
												lS?.get("periscope")?.data?.[bizId]?.bizSubscription) ? (
												<ItemIssuesRoutes />
											) : (
												this.state.isPeriscopeEarlyAccessEnabled !== undefined && (
													<Redirect to="/access-denied" />
												)
											)
										}
									/>

									<Route
										exact
										path="/periscope/activity-log"
										render={(props) =>
											this.state.isPeriscopeEnabled &&
											(this.props.periscopeEarlyAccessState?.bizSubscription ||
												lS?.get("periscope")?.data?.[bizId]?.bizSubscription) ? (
												<ActivityLog />
											) : (
												this.state.isPeriscopeEarlyAccessEnabled !== undefined && (
													<Redirect to="/access-denied" />
												)
											)
										}
									/>
									<Route path="/periscope/collect" render={(props) => <WaitingPage />} />

									<Route
										path="/periscope/early-access"
										render={(props) => {
											const isAdmin = access?.isAdmin;
											const isInternalUser =
												this.props?.login?.loginDetail?.email?.includes("@urbanpiper.com");

											const showWaitlistCTA =
												this.state.isPeriscopeEnabled &&
												!this.state.isPeriscopeEarlyAccessEnabled &&
												!lS?.get("periscope")?.data?.[bizId]?.bizSubscription &&
												!isInternalUser;

											const showEarlyAccessSignUp =
												this.state.isPeriscopeEnabled &&
												this.state.isPeriscopeEarlyAccessEnabled &&
												!lS?.get("periscope")?.data?.[bizId]?.bizSubscription;

											if (!isAdmin || !isFeatureFlagEnabled("enable-periscope")) {
												return <Redirect to="/access-denied" />;
											}

											if (showEarlyAccessSignUp) {
												return (
													<UserEarlyAccess
														waitlist={false}
														periscopeFeatureFlag={this.state.isPeriscopeEarlyAccessEnabled}
													/>
												);
											}

											return (
												<UserEarlyAccess
													waitlist={true}
													showWaitlistCTA={showWaitlistCTA}
													periscopeFeatureFlag={this.state.isPeriscopeEarlyAccessEnabled}
												/>
											);
										}}
									/>
									<Route path="/error" component={ErrorPage} />
									<Route path="/access-denied" component={AccessDenied} />
									<Redirect to="/error" />
								</Switch>
							</div>
						) : (
							<div className="content-container">
								<Switch>
									<Route
										exact
										path="/"
										render={() => (
											<Redirect to={access.isNonHqAnalytics ? "/analytics/home" : "/reports"} />
										)}
									/>
									<Route
										path="/analytics"
										render={(props) =>
											!isBizDeactivated && access.isNonHqAnalytics ? (
												<AnalyticsContainer />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route
										path="/reports"
										render={(props) =>
											!isBizDeactivated && access.isNonHqReport ? (
												<ReportsNew hasAccess={access.isNonHqReport} />
											) : (
												<Redirect to="/access-denied" />
											)
										}
									/>
									<Route path="/error" component={ErrorPage} />
									<Route path="/access-denied" component={AccessDenied} />
									<Redirect to={access.isNonHqAnalytics ? "/analytics/home" : "/reports"} />
								</Switch>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
}

const ScrollToTop = ({ location }) => {
	useEffect(() => {
		scroll({ top: 0, left: 0 });
	}, [location.pathname]);
	return null;
};

export default App;
